import React, { useCallback, useEffect, useState } from 'react';
import { helpWhy } from '../../help/why';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Button, Chip, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, DialogContentText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { FestivalBonusService } from '../../services/festivalBonus.service';
import { useToast } from '../../context/ToastContext';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import {
    ICreateFestivalBonusGrantRequest,
    IFestivalBonusGrant,
    IFestivalBonusPreview,
} from '../../interfaces/payroll.interface';
import FestivalBonusFormDialog from './FestivalBonusFormDialog';
import FestivalBonusPreviewDrawer from './FestivalBonusPreviewDrawer';
import SearchableSelect from '../common/SearchableSelect';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

// Festival bonus grant management (spec §2.1/§3): list, create, eligibility preview and
// pending-only delete. `appliedAt` is informational (D12) — delete is refused server-side by
// whether payroll rows exist for the grant's month (`monthGenerated`, M-2), never by `appliedAt`
// itself. A grant can be `monthGenerated` with `appliedAt` still null (the run had zero eligible
// employees); such a grant can never be applied OR deleted, so the delete icon and badge below key
// off `monthGenerated`, not just `appliedAt`, to avoid promising an action the backend will refuse.
const FestivalBonusList: React.FC = () => {
    const [grants, setGrants] = useState<IFestivalBonusGrant[]>([]);
    const [year, setYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [preview, setPreview] = useState<IFestivalBonusPreview | null>(null);
    const [voidTarget, setVoidTarget] = useState<IFestivalBonusGrant | null>(null);
    const [voidReason, setVoidReason] = useState('');
    const [voiding, setVoiding] = useState(false);
    const canManagePayroll = usePermission(PERMISSIONS.PAYROLL_MANAGE);
    const { showSuccess, showApiError } = useToast();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();

    const load = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            const data = await FestivalBonusService.listGrants(year, institutionId);
            setGrants(data);
            setLoadFailed(false);
        } catch (error) {
            showApiError(error, 'Failed to load festival bonus grants');
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, [institutionId, year, showApiError]);

    useEffect(() => {
        load();
    }, [load]);

    const handleCreate = async (data: ICreateFestivalBonusGrantRequest) => {
        setCreating(true);
        try {
            const created = await FestivalBonusService.createGrant(data, institutionId);
            showSuccess('Festival bonus grant created');
            setFormOpen(false);
            // M-3: the dialog's Target Year field is free of the list's own year filter, so a
            // grant created for a year the list isn't showing would otherwise refetch and vanish.
            // Switching the filter to the created grant's year keeps it visible; `load()` re-runs
            // via the `year` effect dependency once this state update lands.
            if (created.targetYear !== year) {
                setYear(created.targetYear);
            } else {
                load();
            }
        } catch (error) {
            // Surface the backend's rule-naming 400 message verbatim (spec §2.1/§2.4) rather than
            // predicting it client-side — the dialog stays open so the admin can adjust and retry.
            showApiError(error, 'Failed to create festival bonus grant');
        } finally {
            setCreating(false);
        }
    };

    const handlePreview = async (grantId: string) => {
        setPreviewOpen(true);
        setPreviewLoading(true);
        setPreview(null);
        try {
            const data = await FestivalBonusService.previewGrant(grantId);
            setPreview(data);
        } catch (error) {
            showApiError(error, 'Failed to load eligibility preview');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleDelete = async (grant: IFestivalBonusGrant) => {
        try {
            await FestivalBonusService.deleteGrant(grant.id);
            showSuccess('Festival bonus grant deleted');
            load();
        } catch (error) {
            showApiError(error, 'Failed to delete festival bonus grant');
        }
    };

    const handleVoid = async () => {
        if (!voidTarget || !voidReason.trim()) return;
        setVoiding(true);
        try {
            await FestivalBonusService.voidGrant(voidTarget.id, voidReason.trim(), institutionId);
            showSuccess('Festival bonus grant voided');
            setVoidTarget(null);
            setVoidReason('');
            load();
        } catch (error) {
            // The backend names the blocker (finalized payroll / draft run to void first) verbatim.
            showApiError(error, 'Failed to void festival bonus grant');
        } finally {
            setVoiding(false);
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="payroll.festivalBonus.page">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }} data-help-section="payroll.festivalBonus.toolbar">
                <Typography variant="h5">Festival Bonus Grants</Typography>
                <Box sx={{ flexGrow: 1 }} />
                {isSystemAdmin && (
                    <SearchableSelect
                        label="Select Institution"
                        data-help="payroll.festivalBonus.institution"
                        options={institutions}
                        value={institutionId}
                        onChange={id => setInstitutionId(id)}
                        getOptionLabel={inst => inst.name}
                        clearable={false}
                        sx={{ minWidth: 220 }}
                    />
                )}
                <FormControl size="small" sx={{ minWidth: 120 }} data-help="payroll.festivalBonus.year">
                    <InputLabel id="festival-bonus-year-label">Year</InputLabel>
                    <Select
                        labelId="festival-bonus-year-label"
                        label="Year"
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                    >
                        {[year - 1, year, year + 1].map((y) => (
                            <MenuItem key={y} value={y}>{y}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                
                <Button data-help="payroll.festivalBonus.new"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setFormOpen(true)}
                    disabled={!institutionId || loadFailed}
                >
                    New Grant
                </Button>
                
            </Box>

            <TableContainer component={Paper} data-help-section="payroll.festivalBonus.table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="payroll.festivalBonus.col.occasion">Occasion</TableCell>
                            <TableCell data-help="payroll.festivalBonus.col.targetMonth">Target Month</TableCell>
                            <TableCell align="right" data-help="payroll.festivalBonus.col.percent">% of Basic</TableCell>
                            <TableCell data-help="payroll.festivalBonus.col.status">Status</TableCell>
                            <TableCell align="right" data-help="payroll.festivalBonus.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">Loading...</TableCell>
                            </TableRow>
                        )}
                        {!loading && loadFailed && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    Failed to load festival bonus grants.
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && !loadFailed && !institutionId && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">Select an institution to view its festival bonus grants</TableCell>
                            </TableRow>
                        )}
                        {!loading && !loadFailed && institutionId && grants.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">No festival bonus grants found</TableCell>
                            </TableRow>
                        )}
                        {!loading && !loadFailed && grants.map((grant) => {
                            const applied = !!grant.appliedAt;
                            // M-2: a grant can have payroll rows for its month (monthGenerated)
                            // without ever being appliedAt — the run had zero eligible employees.
                            // Such a grant can never be applied OR deleted; the badge must say so
                            // rather than calling it amber "Pending", which promises a delete the
                            // server will refuse.
                            const deadNotApplied = !applied && grant.monthGenerated;
                            const canDelete = !applied && !grant.monthGenerated;
                            return (
                                <TableRow key={grant.id}>
                                    <TableCell>{grant.occasion}</TableCell>
                                    <TableCell>{MONTHS[grant.targetMonth - 1]} {grant.targetYear}</TableCell>
                                    <TableCell align="right">{Number(grant.percentOfBasic).toFixed(2)}%</TableCell>
                                    <TableCell>
                                        {applied && <Chip size="small" label="Applied" color="success" />}
                                        {!applied && deadNotApplied && (
                                            <Chip
                                                size="small"
                                                label="Not applied — month generated"
                                                color="default"
                                                title="Payroll has already been generated for this month with no eligible employees. This grant can no longer be applied or deleted."
                                            />
                                        )}
                                        {!applied && !deadNotApplied && (
                                            <Chip size="small" label="Pending" color="warning" />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Preview eligibility">
                                            <IconButton
                                                size="small"
                                                data-help="payroll.festivalBonus.action.preview"
                                                aria-label={`Preview eligibility for ${grant.occasion}`}
                                                onClick={() => handlePreview(grant.id)}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {canManagePayroll && (
                                            <Tooltip title="Void grant (wrong month / amount)">
                                                <IconButton
                                                    size="small"
                                                    data-help="payroll.festivalBonus.action.void"
                                                    aria-label={`Void ${grant.occasion}`}
                                                    onClick={() => { setVoidReason(''); setVoidTarget(grant); }}
                                                >
                                                    <BlockIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {canDelete && (
                                            <Tooltip title="Delete grant">
                                                <IconButton
                                                    size="small"
                                                    data-help="payroll.festivalBonus.action.delete"
                                                    aria-label={`Delete ${grant.occasion}`}
                                                    onClick={() => handleDelete(grant)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <FestivalBonusFormDialog
                open={formOpen}
                onClose={() => !creating && setFormOpen(false)}
                onSave={handleCreate}
                saving={creating}
            />

            <Dialog open={!!voidTarget} onClose={() => !voiding && setVoidTarget(null)} PaperProps={{ 'data-help-page': 'payroll.festivalBonus.voidForm.page' } as any} fullWidth maxWidth="sm">
                <DialogTitle>Void festival bonus grant</DialogTitle>
                <DialogContent data-help-section="payroll.festivalBonus.voidForm.section.details">
                    <DialogContentText sx={{ mb: 2 }}>
                        Voiding {voidTarget?.occasion} frees its slot in the yearly grant limit so a corrected
                        grant can be created. If payroll for its month already contains the bonus, void or
                        regenerate that draft payroll run first.
                    </DialogContentText>
                    <TextField
                        data-help="payroll.festivalBonus.voidReason"
                        label="Reason"
                        value={voidReason}
                        onChange={(e) => setVoidReason(e.target.value)}
                        required
                        multiline
                        minRows={2}
                        fullWidth
                        inputProps={{ maxLength: 500 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button data-help="payroll.festivalBonus.voidCancel" onClick={() => setVoidTarget(null)} disabled={voiding}>Cancel</Button>
                    <Button color="error" variant="contained" data-help="payroll.festivalBonus.voidConfirm" {...helpWhy(voiding ? { kind: 'status', message: 'Voiding is in progress.' } : !voidReason.trim() ? { kind: 'status', message: 'Enter a reason first.' } : null)} onClick={handleVoid}
                            disabled={voiding || !voidReason.trim()}>
                        Void grant
                    </Button>
                </DialogActions>
            </Dialog>

            <FestivalBonusPreviewDrawer
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                loading={previewLoading}
                preview={preview}
            />
        </Box>
    );
};

export default FestivalBonusList;
